if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.productFormId = this.dataset.productFormId;
      this.currentVariantId = this.dataset.initialVariantId;
      this.isAvailable = this.dataset.initialAvailable === 'true';
      this.currentSellingPlan = null;

      this.button = this.querySelector('.sticky-atc__submit');
      this.spinnerEl = this.querySelector('.loading-overlay__spinner');
      this.priceEl = this.querySelector('.sticky-atc__price');

      this.isButtonVisible = false;
      this.isFooterVisible = false;
      this.buttonWasScrolledPast = false;
    }

    connectedCallback() {
      this.productForm = document.getElementById(this.productFormId);
      this.mainButton = this.productForm?.querySelector('button[type="submit"]');

      this.setupObservers();
      this.setupEventListeners();
      this.button.addEventListener('click', () => this.addToCart());

      this.syncSellingPlan();
      this.syncPrice();

      // Initialize preorder state from data attribute
      const isPreorder = this.dataset.isPreorder === 'true';
      this.updatePreorderState(isPreorder);
    }

    disconnectedCallback() {
      this.formObserver?.disconnect();
      this.footerObserver?.disconnect();
      this.buttonTextObserver?.disconnect();
      this.sellingPlanObserver?.disconnect();
    }

    setupObservers() {
      const targetElement = this.mainButton || this.productForm;

      if (targetElement) {
        let isFirstCallback = true;

        this.formObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const wasVisible = this.isButtonVisible;
              this.isButtonVisible = entry.isIntersecting;

              if (isFirstCallback) {
                isFirstCallback = false;
                return;
              }

              if (entry.isIntersecting && !wasVisible) {
                this.buttonWasScrolledPast = false;
              }

              if (!entry.isIntersecting && wasVisible && targetElement.getBoundingClientRect().bottom < 0) {
                this.buttonWasScrolledPast = true;
              }

              this.updateVisibility();
            });
          },
          { threshold: 0 }
        );
        this.formObserver.observe(targetElement);
      }

      const footer = document.querySelector('footer');
      if (footer) {
        this.footerObserver = new IntersectionObserver(
          (entries) => {
            this.isFooterVisible = entries[0].isIntersecting;
            this.updateVisibility();
          },
          { threshold: 0 }
        );
        this.footerObserver.observe(footer);
      }
    }

    setupEventListeners() {
      const variantInput = this.productForm?.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.addEventListener('change', (e) => this.handleVariantChange(e.target.value));
      }

      document.addEventListener('variant:change', (e) => {
        if (e.detail?.variant?.id) this.handleVariantChange(e.detail.variant.id);
      });

      document.querySelectorAll('variant-radios, variant-selects').forEach((selector) => {
        selector.addEventListener('change', () => setTimeout(() => this.syncWithCurrentVariant(), 50));
      });

      window.addEventListener('popstate', () => {
        this.syncFromURL();
        this.syncSellingPlan();
        this.syncPrice();
      });

      document.addEventListener('change', (e) => {
        const isRechargeInput =
          e.target.name === 'selling_plan' ||
          e.target.name === 'purchase_type' ||
          e.target.name === 'purchaseOption' ||
          e.target.classList?.contains('rc-radio__input');

        if (isRechargeInput) {
          setTimeout(() => {
            this.syncSellingPlan();
            this.syncPrice();
          }, 150);
        }
      });

      document.addEventListener('click', (e) => {
        if (e.target.closest('[class*="rc-radio"]')) {
          setTimeout(() => {
            this.syncSellingPlan();
            this.syncPrice();
          }, 200);
        }
      });

      if (this.mainButton) {
        this.buttonTextObserver = new MutationObserver(() => this.syncPrice());
        this.buttonTextObserver.observe(this.mainButton, { childList: true, subtree: true, characterData: true });
      }

      this.watchSellingPlanInput();
    }

    watchSellingPlanInput() {
      const sellingPlanInput = this.productForm?.querySelector('input[name="selling_plan"]');

      if (sellingPlanInput) {
        this.observeSellingPlan(sellingPlanInput);
      } else if (this.productForm) {
        const formObserver = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
              if (node.nodeType === 1) {
                const input = node.name === 'selling_plan' ? node : node.querySelector?.('input[name="selling_plan"]');
                if (input) {
                  this.observeSellingPlan(input);
                  formObserver.disconnect();
                  return;
                }
              }
            }
          }
        });
        formObserver.observe(this.productForm, { childList: true, subtree: true });
      }
    }

    observeSellingPlan(input) {
      this.sellingPlanObserver = new MutationObserver(() => {
        this.syncSellingPlan();
        this.syncPrice();
      });
      this.sellingPlanObserver.observe(input, { attributes: true, attributeFilter: ['value'] });

      const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
      Object.defineProperty(input, 'value', {
        get() {
          return descriptor.get.call(this);
        },
        set: (newVal) => {
          descriptor.set.call(input, newVal);
          setTimeout(() => {
            this.syncSellingPlan();
            this.syncPrice();
          }, 50);
        },
      });
    }

    syncSellingPlan() {
      const sellingPlanInput = this.productForm?.querySelector('input[name="selling_plan"]');
      this.currentSellingPlan =
        sellingPlanInput?.value || new URLSearchParams(window.location.search).get('selling_plan') || null;
    }

    syncPrice() {
      if (!this.priceEl) return;

      this.syncSellingPlan();

      if (this.currentSellingPlan) {
        const subscriptionPriceEl = document.querySelector('.rc-radio__price, .rc_block__type--active .rc_price');
        if (subscriptionPriceEl) {
          const priceMatch = subscriptionPriceEl.textContent.match(/\$[\d,.]+/);
          if (priceMatch) {
            this.priceEl.innerHTML = `<span class="price-item--sale">${priceMatch[0]}</span>`;
            return;
          }
        }
      }

      const buttonPrice = this.extractPrice(this.mainButton);
      if (buttonPrice) {
        this.priceEl.innerHTML = `<span class="price-item--sale">${buttonPrice}</span>`;
      }
    }

    // The add-to-cart button can carry a struck-through compare price ahead of
    // the real one, so read the price element rather than the first $ in the text.
    extractPrice(root) {
      if (!root) return null;
      const priceEl =
        root.querySelector('.price-item--sale') || root.querySelector('.price-item--regular') || root;
      const match = priceEl.textContent.match(/\$[\d,.]+/);
      return match ? match[0] : null;
    }

    updateVisibility() {
      this.classList.toggle('is-visible', this.buttonWasScrolledPast && !this.isButtonVisible && !this.isFooterVisible);
    }

    handleVariantChange(variantId) {
      if (!variantId || variantId === this.currentVariantId) return;
      this.currentVariantId = variantId;
      this.syncSellingPlan();
      this.syncPrice();
      this.fetchVariantData(variantId);
    }

    syncWithCurrentVariant() {
      const variantInput = document.querySelector(`#${this.productFormId} input[name="id"]`);
      if (variantInput && variantInput.value !== this.currentVariantId) {
        this.handleVariantChange(variantInput.value);
      }
    }

    syncFromURL() {
      const variantId = new URLSearchParams(window.location.search).get('variant');
      if (variantId && variantId !== this.currentVariantId) {
        this.handleVariantChange(variantId);
      }
    }

    async fetchVariantData(variantId) {
      try {
        const sellingPlanParam = this.currentSellingPlan ? `&selling_plan=${this.currentSellingPlan}` : '';
        const response = await fetch(
          `${window.location.pathname}?variant=${variantId}${sellingPlanParam}&section_id=${this.sectionId}`
        );
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

        const addButton = doc.querySelector('.product-form__submit.native-add-button') ||
          doc.querySelector('.product-form__submit');
        if (addButton) {
          const price = this.extractPrice(addButton);
          if (price && this.priceEl) {
            this.priceEl.innerHTML = `<span class="price-item--sale">${price}</span>`;
          }
          this.isAvailable = !addButton.disabled;
          this.button.disabled = !this.isAvailable;
        }

        const hasPreorder = doc.querySelector('.has-preorder');
        this.updatePreorderState(hasPreorder !== null);
      } catch (error) {
        console.error('Sticky ATC: Failed to fetch variant data', error);
      }
    }

    updatePreorderState(isPreorder) {
      this.classList.toggle('is-preorder', isPreorder);
      this.querySelectorAll('.preorder-show').forEach((el) => (el.style.display = isPreorder ? 'flex' : 'none'));
      this.querySelectorAll('.preorder-hide').forEach((el) => (el.style.display = isPreorder ? 'none' : 'flex'));
    }

    async addToCart() {
      if (!this.isAvailable || this.button.disabled) return;

      this.syncSellingPlan();

      const qtyInput = document.querySelector('quantity-input.pdp-quantity input');
      const quantity = qtyInput ? parseInt(qtyInput.value, 10) : 1;

      this.button.classList.add('loading');
      this.spinnerEl.classList.remove('hidden');
      this.button.disabled = true;

      try {
        const itemData = { id: parseInt(this.currentVariantId, 10), quantity };
        if (this.currentSellingPlan) {
          itemData.selling_plan = parseInt(this.currentSellingPlan, 10);
        }

        const formData = { items: [itemData] };
        const cart = document.querySelector('cart-drawer-v2') || document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        if (cart?.getSectionsToRender) {
          formData.sections = cart.getSectionsToRender().map((s) => s.id);
          formData.sections_url = window.location.pathname;
        }

        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(formData),
        });

        const data = await response.json();

        if (data.status === 422) {
          this.isAvailable = false;
          this.button.disabled = true;
        } else if (cart?.renderContents) {
          cart.renderContents(data);
        } else {
          window.location.href = window.routes.cart_url;
        }
      } catch (error) {
        console.error('Sticky ATC: Add to cart error', error);
      } finally {
        this.button.classList.remove('loading');
        this.spinnerEl.classList.add('hidden');
        this.button.disabled = !this.isAvailable;
      }
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
